import { useState } from "react";
import { Link } from "react-router-dom";
import { useKundenakte, useKundenlinkSichern } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";

/**
 * Der eine Link für den Kunden — seine Kundenakte (Etappe C, 09.10.2026).
 *
 * Vorher gab es drei: den Angebotslink, den Link der Ausfertigung und die
 * Akte. Jetzt bekommt der Kunde nur die Akte; dort steht ein offenes Angebot
 * oben, danach Vertrag, Dokumente, Einsätze und Nachweise. Gibt es noch keinen
 * Link, legt „Kundenlink kopieren" ihn an — ohne einen bestehenden zu ersetzen.
 * Erneuern und Zurückziehen bleiben beim Kunden („Kundenzugang").
 */
export default function Kundenlink({ kundeId, verwalten = true }: { kundeId: string; verwalten?: boolean }) {
  const { data: akte } = useKundenakte(kundeId);
  const sichern = useKundenlinkSichern(kundeId);
  const [kopiert, setKopiert] = useState(false);
  const [fehler, setFehler] = useState("");
  const link = akte?.portal?.link ?? "";

  async function kopieren() {
    setFehler("");
    try {
      const ziel = link || (await sichern.mutateAsync()).link;
      await navigator.clipboard.writeText(ziel);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1800);
    } catch (e) {
      setFehler(fehlertext(e) || "Konnte den Link nicht kopieren — bitte im Feld markieren.");
    }
  }

  return (
    <div className="pf-kundenlink">
      <div className="pf-kundenlink-zeile">
        <input readOnly className="pf-input" aria-label="Kundenlink"
          value={link || "Wird beim ersten Kopieren oder Senden angelegt"}
          onFocus={e => e.currentTarget.select()} />
        <button type="button" className="pf-btn is-ghost" disabled={sichern.isPending} onClick={kopieren}>
          {kopiert ? "✓ Kopiert" : sichern.isPending ? "Lege an…" : "Kundenlink kopieren"}
        </button>
        {link && <a className="pf-btn is-ghost" href={link} target="_blank" rel="noopener noreferrer">Öffnen</a>}
      </div>
      <p className="pf-klein">
        Ein Link für alles: Angebot und Zustimmung, danach Vertrag, Termine und Nachweise.
      </p>
      {verwalten && (
        <Link className="pf-kundenlink-verwalten" to={`/kunden?kunde=${encodeURIComponent(kundeId)}#zugang`}>
          Zugang verwalten (neu ausstellen, zurückziehen) →
        </Link>
      )}
      {fehler && <p className="pk-fehler">{fehler}</p>}
    </div>
  );
}
